import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle, AlertTriangle } from 'lucide-react';
import { useUserStore } from '@/lib/user-store';
import { useTranslation } from '@/hooks/useTranslation';
import { checkPassword } from '@/lib/password-policy';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const resetPassword = useUserStore((s) => s.resetPassword);

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [status, setStatus] = useState<'form' | 'success' | 'error'>('form');

  const isValid = useMemo(() => {
    if (!token || !email) return false;
    return true;
  }, [token, email]);

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!password.trim() || !checkPassword(password).ok) return;
    if (password !== confirmPassword) return;
    if (submitting) return;
    setSubmitting(true);
    const success = await resetPassword(email, token, password);
    setSubmitting(false);
    setStatus(success ? 'success' : 'error');
  };

  // Invalid link (no token or email)
  if (!isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md text-center"
        >
          <div className="mx-auto w-16 h-16 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-6">
            <AlertTriangle className="text-red-500" size={32} />
          </div>
          <h1 className="text-2xl font-syncopate font-bold uppercase tracking-tight mb-3">
            {t.auth.invalidResetLink}
          </h1>
          <p className="text-sm text-black/50 dark:text-white/50 mb-8">
            {t.auth.invalidResetLink}
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:opacity-90 transition-all"
          >
            {t.auth.forgotPasswordTitle}
          </Link>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
            className="mx-auto w-16 h-16 rounded-full bg-green-50 dark:bg-green-500/10 flex items-center justify-center mb-6"
          >
            <CheckCircle className="text-green-500" size={32} />
          </motion.div>
          <h1 className="text-2xl font-syncopate font-bold uppercase tracking-tight mb-3">
            {t.auth.passwordResetSuccess}
          </h1>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:opacity-90 transition-all mt-6"
          >
            {t.auth.goToLogin}
          </Link>
        </motion.div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md text-center"
        >
          <div className="mx-auto w-16 h-16 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-6">
            <AlertTriangle className="text-red-500" size={32} />
          </div>
          <h1 className="text-2xl font-syncopate font-bold uppercase tracking-tight mb-3">
            {t.auth.invalidResetLink}
          </h1>
          <p className="text-sm text-black/50 dark:text-white/50 mb-8">
            {t.auth.invalidResetLink}
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:opacity-90 transition-all"
          >
            {t.auth.forgotPasswordTitle}
          </Link>
        </motion.div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="mx-auto w-14 h-14 rounded-full bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center mb-5">
            <Lock className="text-black/40 dark:text-white/40" size={24} />
          </div>
          <h1 className="text-2xl font-syncopate font-bold uppercase tracking-tight">
            {t.auth.forgotPasswordTitle}
          </h1>
          <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.3em] text-black/30 dark:text-white/30">
            {email}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 sm:p-8 space-y-5">
          <div>
            <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2">
              {t.auth.newPassword}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3.5 pr-12 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium focus:outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20"
                placeholder="••••••"
                minLength={8}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20 hover:text-black/50 dark:hover:text-white/50 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {password.length > 0 && !checkPassword(password).ok && (
              <p className="mt-1.5 text-[10px] text-red-500">{t.auth.passwordMinLength}</p>
            )}
          </div>

          <div>
            <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2">
              {t.auth.confirmPassword}
            </label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3.5 pr-12 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium focus:outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20"
                placeholder="••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20 hover:text-black/50 dark:hover:text-white/50 transition-colors"
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {confirmPassword.length > 0 && password !== confirmPassword && (
              <p className="mt-1.5 text-[10px] text-red-500">{t.auth.passwordsDontMatch}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={!checkPassword(password).ok || password !== confirmPassword}
            className="w-full flex items-center justify-center gap-2 py-4 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.3em] rounded-xl hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-30"
          >
            <Lock size={14} />
            {t.auth.resetPasswordButton}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
